import React, { forwardRef, useImperativeHandle, useRef, useEffect } from 'react';
import { Table } from '@ajaibid/components';

import Detail from './components/Detail';
import useColumns from './hooks/useColumns';
import useHistoryRegular from './hooks/useHistoryRegular';
import { styTable, styWrapper, stySentinel } from './style';

/**
 * @typedef {import('@services/api/stock/history/regular').HistoryRegular} HistoryRegular
 * @typedef {import('@ajaibid/components').TableProps<HistoryRegular>['getRowId']} GetRowId
 */

/**
 * @type {GetRowId}
 */
function getRowId(row, ix, parent) {
  return `${parent ? `${parent?.id}__` : ''}${row?.idx_trade_no || ix}`; // Real one
}

function renderRowExpand(row) {
  return <Detail item={row.original} />;
}

const StockHistoryRegular = forwardRef((_, ref) => {
  const {
    data,
    isInitialLoading,
    refetch,
    isRefetching,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useHistoryRegular();

  const columns = useColumns();
  const sentinelRef = useRef(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isFetchingNextPage && !hasFetchedRef.current) {
            hasFetchedRef.current = true;
            fetchNextPage();
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);

    return () => {
      observer.unobserve(sentinel);
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    if (!isFetchingNextPage) {
      hasFetchedRef.current = false;
    }
  }, [isFetchingNextPage]);

  useImperativeHandle(ref, () => ({
    // Allow the following functions & variables to be called from its parents
    refetchTable: refetch,
    isRefetching,
  }));

  return (
    <div className={styWrapper}>
      <Table
        className={styTable}
        data={data}
        columns={columns}
        options={{ isHeaderVisible: true, isRowExpandable: true }}
        fetchNext={fetchNextPage}
        getRowId={getRowId}
        isInitialLoading={isInitialLoading}
        isFetchingNext={isFetchingNextPage}
        renderRowExpand={renderRowExpand}
        emptyStateContent="Mulai bertransaksi dan lihat statusnya di sini."
      />
      <div ref={sentinelRef} className={stySentinel} />
    </div>
  );
});

export default StockHistoryRegular;
